/**
 * @typedef SearchContext
 * @property {string} culture
 * @property {string} taxonomyRootNodeGUID
 * @property {number} pageSize
 * @property {string} cdnDomain
 * @property {string} searchTerm
 * @property {string[]} taxonomies
 * @property {string} queryKeyTerm
 * @property {string} queryKeyIdentifiers
 */

/**
 * @typedef SearchContent
 * @property {string} items
 * @property {string} orderBy
 * @property {string} relevance
 * @property {string} alphabetically
 * @property {string} filters
 * @property {string} close
 * @property {string} score
 * @property {string} results
 * @property {string} result
 * @property {string} removeSearchTermFilter
 * @property {string} removeFilter
 */

/**
 * @typedef SearchRequest
 * @property {string} term
 * @property {{[key: number]: number[]}} filters
 * @property {string} orderBy
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {string} taxonomyRootNodeGUID
 */

/**
 * @typedef FacetResponse
 * @property {number} value
 * @property {string} name
 * @property {string} id
 * @property {string} identifier
 * @property {number} count
 * @property {boolean} isSelected
 */

/**
 * @typedef FacetGroup
 * @property {number} value
 * @property {string} id
 * @property {string} name
 * @property {FacetResponse[]} facets
 */

/**
 * @typedef SearchResult
 * @property {string} id
 * @property {string} pagePath
 * @property {string} title
 * @property {string} titleHTML
 * @property {string} imagePath
 * @property {string} descriptionHTML
 * @property {string=} date
 * @property {Object} customContent
 * @property {string} modifiedWhen
 * @property {number} score
 * @property {string} pageType
 */

/**
 * @typedef {'INITIALIZING'|'LOADING'|'LOADED'} ActionState
 */

/**
 * @typedef SearchResponse
 * @property {FacetGroup[]} groups
 * @property {SearchResult[]} results
 * @property {{ nodeGuid: string, name: string }[]} contentTypeTaxonomies
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalCount
 */

/**
 * @typedef OrderByOptions
 * @property {{ name: string, value:string }} alphabetical
 * @property {{ name: string, value:string }} relevance
 */

/**
 * @typedef SearchState
 * @property {SearchRequest} request
 * @property {SearchResponse} response
 * @property {OrderByOptions} orderByOptions
 * @property {ActionState} actionState,
 * @property {AbortController} abortController
 * @property {number} maxRelevance
 * @property {SearchContent} content
 * @property {string} culture
 * @property {string} cdnDomain
 * @property {string} queryKeyTerm
 * @property {string} queryKeyIdentifiers
 */

/**
 * @type {OrderByOptions}
 */
const orderByOptions = {
  relevance: {
    name: 'Relevance',
    value: 'Relevance',
  },
  alphabetical: {
    name: 'Alphabetical',
    value: 'Alphabetical',
  },
};

/**
 * @type {SearchState}
 */
export const state = {
  request: {
    term: '',
    filters: {},
    orderBy: orderByOptions.relevance.value,
    pageNumber: 1,
    pageSize: 10,
    taxonomyRootNodeGUID: '',
  },
  response: {
    totalCount: 0,
    pageNumber: 0,
    pageSize: 0,
    groups: [],
    results: [],
    contentTypeTaxonomies: [
      { name: 'product', nodeGuid: '' },
      { name: 'literature', nodeGuid: '' },
      { name: 'video', nodeGuid: '' },
      { name: 'pressReleaseNews', nodeGuid: '' },
    ],
  },
  actionState: 'INITIALIZING',
  maxRelevance: 0,
  abortController: new AbortController(),
  content: {
    alphabetically: '',
    items: '',
    close: '',
    filters: '',
    relevance: '',
    removeFilter: '',
    removeSearchTermFilter: '',
    result: '',
    results: '',
    score: '',
    orderBy: '',
  },
  culture: 'en-US',
  cdnDomain: '',
  orderByOptions,
  queryKeyTerm: '',
  queryKeyIdentifiers: '',
};
